// components/OpenInAppBanner.jsx
// Smart banner for mobile web that prompts users to open in the native app

import React, { useState, useEffect } from 'react';
import { X, Smartphone } from 'lucide-react';
import { getSchemeUrl } from '../utils/deepLinkConfig';

const BANNER_DISMISSED_KEY = 'gweffit_app_banner_dismissed';
const DISMISS_DURATION = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Detect if user is on mobile web (not in native app)
 */
function isMobileWeb() {
  if (typeof window === 'undefined') return false;

  // Don't show if we're in the native app
  if (window.Capacitor?.isNativePlatform?.()) return false;

  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod|Android/i.test(ua);
}

/**
 * Check if banner was recently dismissed
 */
function wasDismissedRecently() {
  try {
    const dismissed = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (!dismissed) return false;

    const dismissedAt = parseInt(dismissed, 10);
    return Date.now() - dismissedAt < DISMISS_DURATION;
  } catch {
    return false;
  }
}

/**
 * Mark banner as dismissed
 */
function dismissBanner() {
  try {
    localStorage.setItem(BANNER_DISMISSED_KEY, Date.now().toString());
  } catch {
    // Ignore storage errors
  }
}

/**
 * OpenInAppBanner - Shows on mobile web pages to prompt native app open
 *
 * @param {string} type - Link type ('trial', 'summons', 'crew', etc.)
 * @param {string} id - The ID for the current content
 * @param {string} title - Optional title to display
 */
export default function OpenInAppBanner({ type = 'trial', id, title }) {
  const [show, setShow] = useState(false);
  const [appOpened, setAppOpened] = useState(false);

  useEffect(() => {
    // Only show on mobile web, if not recently dismissed, and if we have an ID
    if (isMobileWeb() && !wasDismissedRecently() && id) {
      // Small delay to avoid flash on page load
      const timer = setTimeout(() => setShow(true), 500);
      return () => clearTimeout(timer);
    }
  }, [id]);

  const handleOpenApp = () => {
    const currentParams = (() => {
      try {
        const search = new URLSearchParams(window.location.search || '');
        const ref = search.get('ref');
        const refSrc = search.get('ref_src');
        const refId = search.get('ref_id');
        return {
          ref: ref || null,
          ref_src: refSrc || null,
          ref_id: refId || null,
        };
      } catch {
        return {};
      }
    })();

    const schemeUrl = getSchemeUrl(type, id, currentParams);
    if (!schemeUrl) return;

    setAppOpened(true);

    // Try to open the app via custom scheme
    // This will fail silently if the app isn't installed
    const start = Date.now();

    // Create hidden iframe to trigger scheme (works better than window.location in some browsers)
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = schemeUrl;
    document.body.appendChild(iframe);

    // Also try direct location change as backup
    setTimeout(() => {
      window.location.href = schemeUrl;
    }, 100);

    // If we're still here after 2 seconds, app probably isn't installed
    setTimeout(() => {
      const elapsed = Date.now() - start;
      if (elapsed < 3000 && document.hasFocus()) {
        // User is still on the page, app likely not installed
        setAppOpened(false);
        // Could show "Get the app" prompt here
      }

      // Clean up iframe
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 2500);

    // Track the attempt
    if (window.trackEvent) {
      window.trackEvent('open_in_app_clicked', { type, id, platform: 'mobile_web' });
    }
  };

  const handleDismiss = () => {
    dismissBanner();
    setShow(false);

    if (window.trackEvent) {
      window.trackEvent('open_in_app_dismissed', { type, id });
    }
  };

  if (!show) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 shadow-lg safe-area-top">
      <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Smartphone size={20} />
          </div>
          <div className="min-w-0">
            <div className="font-bold text-sm truncate">
              {title || 'GweffIT'}
            </div>
            <div className="text-xs text-white/80">
              Better experience in the app
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleOpenApp}
            disabled={appOpened}
            className="px-4 py-2 bg-white text-purple-600 rounded-full font-bold text-sm hover:bg-purple-50 transition-colors disabled:opacity-50"
          >
            {appOpened ? 'Opening...' : 'Open'}
          </button>

          <button
            onClick={handleDismiss}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Dismiss"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to add padding to page content when banner is shown
 */
export function useOpenInAppBannerPadding() {
  const [padding, setPadding] = useState(0);

  useEffect(() => {
    if (isMobileWeb() && !wasDismissedRecently()) {
      setPadding(60); // Approximate banner height
    }
  }, []);

  return padding;
}
